import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate, useNavigate } from 'react-router-dom';
import { Grid, Box, CircularProgress, Typography, Paper, Chip, Stack, Tooltip, Button, useMediaQuery } from '@mui/material';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import { differenceInMinutes } from 'date-fns';
import Header from './components/Header';
import SiteSelector from './components/SiteSelector';
import Controls from './components/Controls';
import CurrentConditions from './components/CurrentConditions';
import SiteContacts from './components/SiteContacts';
import WindSpeedHistory from './components/WindSpeedHistory';
import ParticulateMatterHistory from './components/ParticulateMatterHistory';
import Login from './components/Login';
import ErrorBoundary from './components/ErrorBoundary';
import ChartErrorBoundary from './components/ChartErrorBoundary';
import AlertManager from './components/AlertManager';
import TinticOperationsBoard from './components/TinticOperationsBoard';
import { useLatestData } from './hooks/useLatestData';
import { useLatestLightningData } from './hooks/useLatestLightningData';
import { useInterval } from './hooks/useInterval';
import { timezones, kphToMph } from './utils/utils';

// Define admin users (same as in SiteContacts.js)
const ADMIN_USERS = [
  'adam.hatch@ivanhoeelectric.com',
  'angelica.folk@ivanhoeelectric.com',
  'nate@tipple.consulting'
];

const getLastSelectedSiteStorageKey = (email) =>
  `ie_dashboard_last_site:${String(email || '').toLowerCase()}`;

const normalizeLightningLevel = (value) => {
  if (value === 'Level 2') return 'Red';
  if (value === 'Level 1') return 'Yellow';
  return value || 'Normal';
};

const Dashboard = ({ supabase, user, setUser, location, setLocation, sites }) => {
  const [dateRange, setDateRange] = useState('last12h');
  const [timezone, setTimezone] = useState('America/Phoenix');
  const [refreshInterval, setRefreshInterval] = useState(0); // Default to Never
  const [openAlertManager, setOpenAlertManager] = useState(false);
  const [alertsDisabled, setAlertsDisabled] = useState(false);
  const [alertThresholds, setAlertThresholds] = useState({
    wind_metric: 'gust',
    wind_threshold_kph: 40.234,
  });
  const [lightningActionLevel, setLightningActionLevel] = useState('Normal');
  const [alertSettingsVersion, setAlertSettingsVersion] = useState(0);
  const useRailLayout = useMediaQuery('(min-width:1040px)');
  const alertsRequestIdRef = useRef(0);

  const navigate = useNavigate();
  const { latestData, loading: latestDataLoading, error: latestDataError } = useLatestData(location, refreshInterval);
  const { lightningData, loading: lightningLoading, error: lightningError } = useLatestLightningData(location, refreshInterval);

  const handleLogout = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error && error.message !== 'Auth session missing!') {
        throw error;
      }
      // Clear user state and navigate to login regardless of session status
      setUser(null);
      navigate('/login');
    } catch (err) {
      console.error('Logout error:', err.message);
      // Even if logout fails, clear the user state and navigate to login
      setUser(null);
      navigate('/login');
    }
  };

  const handleManageAlerts = () => {
    setOpenAlertManager(true);
  };

  const handleLocationChange = useCallback((_, newValue) => {
    if (!newValue) return;
    setLocation(newValue);

    if (user?.email) {
      try {
        localStorage.setItem(getLastSelectedSiteStorageKey(user.email), newValue);
      } catch (error) {
        console.warn('Failed to store last selected site:', error);
      }
    }
  }, [setLocation, user]);

  const handleAlertSettingsChange = () => {
    setAlertSettingsVersion((prev) => prev + 1);
  };

  const fetchAlertsStatus = useCallback(async () => {
    if (!location || !supabase) return;

    alertsRequestIdRef.current += 1;
    const requestId = alertsRequestIdRef.current;

    try {
      const [
        { data, error },
        { data: actionData, error: actionError },
      ] = await Promise.all([
        supabase
          .from('alert_thresholds')
          .select('is_active, wind_metric, wind_threshold_kph')
          .eq('site_name', location)
          .single(),
        supabase
          .from('current_action_levels')
          .select('lightning_action_level')
          .eq('site_name', location)
          .single(),
      ]);

      if (requestId !== alertsRequestIdRef.current) {
        return;
      }

      if (error && error.code !== 'PGRST116') {
        throw error;
      }
      if (actionError && actionError.code !== 'PGRST116') {
        throw actionError;
      }

      setAlertsDisabled(Boolean(data && data.is_active === false));
      setAlertThresholds({
        wind_metric: data?.wind_metric || 'gust',
        wind_threshold_kph: data?.wind_threshold_kph || 40.234,
      });
      setLightningActionLevel(normalizeLightningLevel(actionData?.lightning_action_level));
    } catch (err) {
      if (requestId !== alertsRequestIdRef.current) {
        return;
      }

      console.error('Error fetching alert status:', err);
      setAlertsDisabled(false);
      setAlertThresholds({
        wind_metric: 'gust',
        wind_threshold_kph: 40.234,
      });
      setLightningActionLevel('Normal');
    }
  }, [location, supabase]);

  useEffect(() => {
    fetchAlertsStatus();
  }, [fetchAlertsStatus, alertSettingsVersion, refreshInterval]);

  // Periodic in-place refresh (no page reload) when interval > 0.
  useInterval(
    () => {
      if (refreshInterval > 0) {
        fetchAlertsStatus();
      }
    },
    refreshInterval > 0 ? refreshInterval * 60 * 1000 : null
  );

  const selectedSite = useMemo(() => 
    sites.find((s) => s.site_name === location) || { latitude: 0, longitude: 0 },
    [sites, location]
  );

  useEffect(() => {
    if (selectedSite?.timezone && selectedSite.timezone !== timezone) {
      setTimezone(selectedSite.timezone);
    }
  }, [selectedSite, timezone]);

  const lightningState = useMemo(() => {
    if (lightningLoading) return 'Loading';
    if (lightningError) return 'N/A';

    try {
      const now = new Date();
      const recentStrikes = (lightningData || []).filter((strike) => {
        const strikeTime = new Date(strike.time_utc.endsWith('Z') ? strike.time_utc : `${strike.time_utc}Z`);
        const ageMinutes = differenceInMinutes(now, strikeTime);
        const distanceMiles = strike.distance_km * 0.621371;
        return ageMinutes <= 30 && distanceMiles <= 20;
      });

      if (recentStrikes.length === 0) {
        return lightningActionLevel === 'Normal' ? 'None within 20 mi' : `${lightningActionLevel} Alert`;
      }

      const nearestStrike = recentStrikes.reduce((prev, curr) =>
        curr.distance_km < prev.distance_km ? curr : prev
      );
      const nearestMiles = nearestStrike.distance_km * 0.621371;

      if (lightningActionLevel === 'Red') {
        return 'Red Alert';
      }
      if (lightningActionLevel === 'Orange') {
        return 'Orange Alert';
      }
      if (lightningActionLevel === 'Yellow') {
        return 'Yellow Alert';
      }

      if (nearestMiles <= 20) {
        return `Recent ${nearestMiles.toFixed(1)} mi`;
      }

      return 'None within 20 mi';
    } catch (error) {
      console.error('Error deriving lightning signal state:', error);
      return 'N/A';
    }
  }, [lightningActionLevel, lightningData, lightningError, lightningLoading]);

  const { windState, pmState } = useMemo(() => {
    if (latestDataLoading) {
      return { windState: 'Loading', pmState: 'Loading' };
    }

    if (latestDataError || !latestData) {
      return { windState: 'N/A', pmState: 'N/A' };
    }

    try {
      const windSpeedMph = latestData.wind_speed_kph ? kphToMph(latestData.wind_speed_kph) : 0;
      const windGustMph = latestData.wind_gust_kph ? kphToMph(latestData.wind_gust_kph) : windSpeedMph;
      const windThresholdMph = (alertThresholds.wind_threshold_kph || 40.234) * 0.621371;
      const usingGustMetric = (alertThresholds.wind_metric || 'gust') === 'gust';
      const windValue = usingGustMetric ? windGustMph : windSpeedMph;
      const windLabel = usingGustMetric ? 'Gust' : 'Speed';
      const calculatedWindState = windValue > windThresholdMph
        ? `High ${windLabel}: ${windValue.toFixed(1)} mph`
        : `Normal: ${windValue.toFixed(1)} mph`;

      const pm25Value = latestData.pm2_5_ugm3 || 0;
      const pm10Value = latestData.pm10_ugm3 || 0;
      const pm25Category = getPMCategory(pm25Value, 'pm25');
      const pm10Category = getPMCategory(pm10Value, 'pm10');
      const calculatedPmState =
        getPMPriority(pm25Category) >= getPMPriority(pm10Category) ? pm25Category : pm10Category;

      return { windState: calculatedWindState, pmState: calculatedPmState };
    } catch (error) {
      console.error('Error deriving wind/PM signal state:', error);
      return { windState: 'N/A', pmState: 'N/A' };
    }
  }, [latestData, latestDataError, latestDataLoading, alertThresholds]);

  if (!location) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '70vh' }}>
        <CircularProgress sx={{ color: 'var(--color-brand)' }} />
      </Box>
    );
  }

  return (
    <div className="phoenix-shell">
      <Header timezone={timezone} siteName={location} />
      <Box className="phoenix-layer">
        <Box className="phoenix-wrap" sx={{ pt: 1.5 }}>
          <Box className="phoenix-grid">
            <Box className="phoenix-rail">
              <Paper className="phoenix-meta phoenix-animate">
                <Typography variant="overline" sx={{ letterSpacing: 1.2, color: 'rgba(11, 79, 74, 0.78)', fontWeight: 800 }}>
                  Site Snapshot
                </Typography>
                <Typography variant="h5" sx={{ lineHeight: 1.05, mb: 0.5 }}>
                  {location}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    color: 'var(--ops-muted)',
                    textTransform: 'uppercase',
                    letterSpacing: 1.05,
                    fontWeight: 700,
                    display: 'block',
                    mb: 0.6,
                  }}
                >
                  Live Site Risk Signals
                </Typography>
                <Stack direction="row" spacing={0.9} sx={{ flexWrap: 'wrap', rowGap: 0.9 }}>
                  {alertsDisabled && (
                    <Chip
                      icon={<WarningAmberRoundedIcon sx={{ color: '#8f4800 !important' }} />}
                      label="Alerts Off"
                      sx={riskChipSx('rgba(214, 129, 34, 0.22)', '#7a4300', 'rgba(214, 129, 34, 0.34)')}
                    />
                  )}
                  <Tooltip
                    arrow
                    title={
                      <Box sx={{ fontSize: '12px' }}>
                        <Box sx={{ fontWeight: 'bold', mb: 1 }}>PM (Worst of PM2.5 & PM10)</Box>
                        <table style={{ borderCollapse: 'collapse', width: '100%' }}>
                          <thead>
                            <tr>
                              <th style={{ textAlign: 'left', padding: '2px 8px 2px 0', borderBottom: '1px solid #ccc' }}></th>
                              <th style={{ textAlign: 'left', padding: '2px 4px', borderBottom: '1px solid #ccc' }}>Category</th>
                              <th style={{ textAlign: 'center', padding: '2px 4px', borderBottom: '1px solid #ccc' }}>PM2.5 (ug/m3)</th>
                              <th style={{ textAlign: 'center', padding: '2px 4px', borderBottom: '1px solid #ccc' }}>PM10 (ug/m3)</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr>
                              <td style={{ padding: '2px 8px 2px 0' }}><span style={{ color: '#00E400' }}>■</span></td>
                              <td style={{ padding: '2px 4px' }}>Good</td>
                              <td style={{ textAlign: 'center', padding: '2px 4px' }}>0-12</td>
                              <td style={{ textAlign: 'center', padding: '2px 4px' }}>0-54</td>
                            </tr>
                            <tr>
                              <td style={{ padding: '2px 8px 2px 0' }}><span style={{ color: '#FFFF00' }}>■</span></td>
                              <td style={{ padding: '2px 4px' }}>Moderate</td>
                              <td style={{ textAlign: 'center', padding: '2px 4px' }}>12.1-35.4</td>
                              <td style={{ textAlign: 'center', padding: '2px 4px' }}>55-154</td>
                            </tr>
                            <tr>
                              <td style={{ padding: '2px 8px 2px 0' }}><span style={{ color: '#FF7E00' }}>■</span></td>
                              <td style={{ padding: '2px 4px' }}>Unhealthy for Sensitive Groups</td>
                              <td style={{ textAlign: 'center', padding: '2px 4px' }}>35.5-55.4</td>
                              <td style={{ textAlign: 'center', padding: '2px 4px' }}>155-254</td>
                            </tr>
                            <tr>
                              <td style={{ padding: '2px 8px 2px 0' }}><span style={{ color: '#FF0000' }}>■</span></td>
                              <td style={{ padding: '2px 4px' }}>Unhealthy</td>
                              <td style={{ textAlign: 'center', padding: '2px 4px' }}>55.5-150.4</td>
                              <td style={{ textAlign: 'center', padding: '2px 4px' }}>255-354</td>
                            </tr>
                            <tr>
                              <td style={{ padding: '2px 8px 2px 0' }}><span style={{ color: '#8F3F97' }}>■</span></td>
                              <td style={{ padding: '2px 4px' }}>Very Unhealthy</td>
                              <td style={{ textAlign: 'center', padding: '2px 4px' }}>150.5-250.4</td>
                              <td style={{ textAlign: 'center', padding: '2px 4px' }}>355-424</td>
                            </tr>
                            <tr>
                              <td style={{ padding: '2px 8px 2px 0' }}><span style={{ color: '#7E0023' }}>■</span></td>
                              <td style={{ padding: '2px 4px' }}>Hazardous</td>
                              <td style={{ textAlign: 'center', padding: '2px 4px' }}>250.5+</td>
                              <td style={{ textAlign: 'center', padding: '2px 4px' }}>425+</td>
                            </tr>
                          </tbody>
                        </table>
                      </Box>
                    }
                  >
                    <Chip label={`Dust: ${pmState}`} sx={riskChipSx(getAQIColor(pmState, 0.25))} />
                  </Tooltip>
                  <Tooltip
                    arrow
                    title={
                      <>
                        <Box sx={{ fontSize: '14px', lineHeight: '1.5' }}>
                          <strong>Lightning Monitoring (30 min, 20 mi):</strong>
                        </Box>
                        <Box sx={{ fontSize: '14px', lineHeight: '1.5' }}>
                          <span style={{ color: '#ef4444' }}>■</span> Red Alert: lightning within 6 mi or 30-minute red hold active
                        </Box>
                        <Box sx={{ fontSize: '14px', lineHeight: '1.5' }}>
                          <span style={{ color: '#f39c12' }}>■</span> Orange Alert: lightning over 6 mi and up to 12 mi
                        </Box>
                        <Box sx={{ fontSize: '14px', lineHeight: '1.5' }}>
                          <span style={{ color: '#facc15' }}>■</span> Yellow Alert: lightning over 12 mi and up to 18 mi
                        </Box>
                        <Box sx={{ fontSize: '14px', lineHeight: '1.5' }}>
                          <span style={{ color: '#60a5fa' }}>■</span> Recent Lightning: strike shown within 20 mi, but no active alert
                        </Box>
                        <Box sx={{ fontSize: '14px', lineHeight: '1.5' }}>
                          <span style={{ color: '#2ecc71' }}>■</span> None within 20 mi: no nearby monitored strikes
                        </Box>
                      </>
                    }
                  >
                    <Chip label={`Lightning: ${lightningState}`} sx={riskChipSx(getLightningColor(lightningState, 0.25))} />
                  </Tooltip>
                  <Tooltip
                    arrow
                    title={
                      <>
                        <Box sx={{ fontSize: '14px', lineHeight: '1.5' }}>
                          <strong>Wind {(alertThresholds.wind_metric || 'gust') === 'gust' ? 'Gust' : 'Speed'} (Latest):</strong>
                        </Box>
                        <Box sx={{ fontSize: '14px', lineHeight: '1.5' }}>
                          <span style={{ color: '#2ecc71' }}>■</span> Normal: Below threshold
                        </Box>
                        <Box sx={{ fontSize: '14px', lineHeight: '1.5' }}>
                          <span style={{ color: '#c0392b' }}>■</span> High: Above site threshold ({((alertThresholds.wind_threshold_kph || 40.234) * 0.621371).toFixed(0)} mph)
                        </Box>
                      </>
                    }
                  >
                    <Chip label={`Wind: ${windState}`} sx={riskChipSx(getWindColor(windState, 0.25))} />
                  </Tooltip>
                </Stack>
                <Typography variant="caption" sx={{ color: 'var(--ops-muted)', fontSize: '0.72rem', mt: 0.7 }}>
                  Hover each signal for threshold details
                </Typography>
              </Paper>
              <Box className="phoenix-animate phoenix-delay-1">
                <SiteSelector
                  sites={sites}
                  value={location}
                  onChange={handleLocationChange}
                  onManageAlerts={handleManageAlerts}
                  supabase={supabase}
                  user={user}
                  layout={useRailLayout ? 'rail' : 'top'}
                />
              </Box>
              <Box className="phoenix-animate phoenix-delay-2">
                <Controls
                  timezone={timezone}
                  onTimezoneChange={(e) => setTimezone(e.target.value)}
                  dateRange={dateRange}
                  onDateRangeChange={(e) => setDateRange(e.target.value)}
                  timezones={timezones}
                  refreshInterval={refreshInterval}
                  onRefreshIntervalChange={(e) => setRefreshInterval(Number(e.target.value))}
                  site={location}
                  supabase={supabase}
                  layout={useRailLayout ? 'rail' : 'top'}
                />
              </Box>
              <Box className="phoenix-animate phoenix-delay-3" sx={{ mt: 'auto', pb: 0.2 }}>
                <Button
                  variant="outlined"
                  fullWidth
                  onClick={handleLogout}
                  sx={{
                    color: 'var(--ops-brand)',
                    borderColor: 'rgba(15, 118, 110, 0.45)',
                    backgroundColor: 'rgba(255, 255, 255, 0.78)',
                    '&:hover': {
                      borderColor: 'var(--ops-brand)',
                      backgroundColor: 'rgba(15, 118, 110, 0.08)',
                    },
                  }}
                >
                  Logout
                </Button>
              </Box>
            </Box>
            <Box className="phoenix-main">
              <Box className="phoenix-stack">
                <Box className="phoenix-animate phoenix-delay-1">
                  <ErrorBoundary>
                    <CurrentConditions
                      site={location}
                      latitude={selectedSite.latitude}
                      longitude={selectedSite.longitude}
                      timezone={timezone}
                      refreshInterval={refreshInterval}
                      latestData={latestData}
                      latestDataLoading={latestDataLoading}
                      latestDataError={latestDataError}
                      lightningData={lightningData}
                      lightningLoading={lightningLoading}
                      lightningError={lightningError}
                    />
                  </ErrorBoundary>
                </Box>
                <Grid container spacing={1.75}>
                  <Grid className="phoenix-animate phoenix-delay-2" item xs={12} xl={6}>
                    <ErrorBoundary>
                      <SiteContacts site={location} user={user} timezone={timezone} />
                    </ErrorBoundary>
                  </Grid>
                  <Grid className="phoenix-animate phoenix-delay-3" item xs={12} xl={6}>
                    <ChartErrorBoundary>
                      <ParticulateMatterHistory site={location} dateRange={dateRange} timezone={timezone} refreshInterval={refreshInterval} />
                    </ChartErrorBoundary>
                  </Grid>
                  <Grid className="phoenix-animate phoenix-delay-4" item xs={12}>
                    <ChartErrorBoundary>
                      <WindSpeedHistory
                        site={location}
                        dateRange={dateRange}
                        timezone={timezone}
                        refreshInterval={refreshInterval}
                        latestData={latestData}
                        latestDataLoading={latestDataLoading}
                        latestDataError={latestDataError}
                      />
                    </ChartErrorBoundary>
                  </Grid>
                </Grid>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
      <AlertManager
        open={openAlertManager}
        onClose={() => setOpenAlertManager(false)}
        selectedSite={location}
        supabase={supabase}
        onSettingsChange={handleAlertSettingsChange}
      />
    </div>
  );
};

const riskChipSx = (backgroundColor, color = '#1a1a1a', borderColor = 'rgba(17, 47, 38, 0.2)') => ({
  fontWeight: 700,
  fontSize: '0.8rem',
  backgroundColor,
  color,
  border: `1px solid ${borderColor}`,
  height: 32,
  borderRadius: 2,
  '& .MuiChip-label': {
    px: 1.05,
  },
  '& .MuiChip-icon': {
    color: `${color} !important`,
    fontSize: 20,
  },
});

const getWindColor = (state, opacity = 1) => {
  if (state.startsWith('High')) {
    return `rgba(192, 57, 43, ${opacity})`;
  }
  if (state.startsWith('Normal')) {
    return `rgba(46, 204, 113, ${opacity})`;
  }
  return `rgba(117, 117, 117, ${opacity})`;
};

const getAQIColor = (state, opacity = 1) => {
  const hexToRgba = (hex, alpha) => {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  };

  switch (state) {
    case 'Good':
      return hexToRgba('#2ecc71', opacity);
    case 'Moderate':
      return hexToRgba('#FFFF00', opacity);
    case 'Unhealthy for Sensitive Groups':
      return hexToRgba('#FF7E00', opacity);
    case 'Unhealthy':
      return hexToRgba('#FF0000', opacity);
    case 'Very Unhealthy':
      return hexToRgba('#8F3F97', opacity);
    case 'Hazardous':
      return hexToRgba('#7E0023', opacity);
    default:
      return `rgba(117, 117, 117, ${opacity})`;
  }
};

const getLightningColor = (state, opacity = 1) => {
  if (state.startsWith('Red')) {
    return `rgba(239, 68, 68, ${opacity})`;
  }
  if (state.startsWith('Orange')) {
    return `rgba(243, 156, 18, ${opacity})`;
  }
  if (state.startsWith('Yellow')) {
    return `rgba(250, 204, 21, ${opacity})`;
  }
  if (state.startsWith('Recent')) {
    return `rgba(96, 165, 250, ${opacity})`;
  }
  if (state === 'None within 20 mi') {
    return `rgba(46, 204, 113, ${opacity})`;
  }
  return `rgba(117, 117, 117, ${opacity})`;
};

const getPMCategory = (value, type) => {
  if (type === 'pm25') {
    if (value <= 12) return 'Good';
    if (value <= 35.4) return 'Moderate';
    if (value <= 55.4) return 'Unhealthy for Sensitive Groups';
    if (value <= 150.4) return 'Unhealthy';
    if (value <= 250.4) return 'Very Unhealthy';
    return 'Hazardous';
  }

  if (value <= 54) return 'Good';
  if (value <= 154) return 'Moderate';
  if (value <= 254) return 'Unhealthy for Sensitive Groups';
  if (value <= 354) return 'Unhealthy';
  if (value <= 424) return 'Very Unhealthy';
  return 'Hazardous';
};

const getPMPriority = (category) => {
  switch (category) {
    case 'Good': return 0;
    case 'Moderate': return 1;
    case 'Unhealthy for Sensitive Groups': return 2;
    case 'Unhealthy': return 3;
    case 'Very Unhealthy': return 4;
    case 'Hazardous': return 5;
    default: return 0;
  }
};

const App = ({ supabase }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [location, setLocation] = useState('');
  const [sites, setSites] = useState([]);

  const tinticSite = useMemo(
    () => sites.find((site) => site.site_name === 'Tintic') || null,
    [sites]
  );
  const tinticSiteName = tinticSite?.site_name || sites[0]?.site_name || 'Tintic';
  const tinticTimezone = tinticSite?.timezone || sites[0]?.timezone || 'America/Denver';

  // Fetch sites based on user's access
  useEffect(() => {
    const fetchUserSites = async () => {
      if (!user?.email) {
        setSites([]);
        return;
      }

      try {
        // Check if user is admin
        const isAdmin = ADMIN_USERS.includes(user.email.toLowerCase());

        let siteList;

        if (isAdmin) {
          // Admin users can see all sites
          const { data: allSites, error: sitesError } = await supabase
            .from('sites')
            .select('site_name, latitude, longitude, timezone, BHP_Alliance')
            .order('site_name');

          if (sitesError) throw sitesError;
          siteList = allSites;
        } else {
          // Regular users only see sites they're contacts for
          // First, get the sites where the user is a contact (case-insensitive email match)
          const { data: userContacts, error: contactsError } = await supabase
            .from('site_contacts')
            .select('site')
            .ilike('email', user.email);

          if (contactsError) throw contactsError;

          if (!userContacts || userContacts.length === 0) {
            setSites([]);
            return;
          }

          // Get the list of site names the user has access to
          const userSiteNames = userContacts.map(contact => contact.site);

          // Then get the full site information for those sites
          const { data: userSites, error: sitesError } = await supabase
            .from('sites')
            .select('site_name, latitude, longitude, timezone, BHP_Alliance')
            .in('site_name', userSiteNames)
            .order('site_name');

          if (sitesError) throw sitesError;
          siteList = userSites;
        }

        setSites(siteList || []);

        // Resolve default site as: saved-per-user -> current valid site -> first accessible site.
        const accessibleSites = siteList || [];
        const hasAccessToSite = (siteName) =>
          Boolean(siteName && accessibleSites.some((site) => site.site_name === siteName));

        let savedSite = null;
        try {
          savedSite = localStorage.getItem(getLastSelectedSiteStorageKey(user.email));
        } catch (error) {
          console.warn('Failed to read last selected site:', error);
        }

        const resolvedLocation = hasAccessToSite(savedSite)
          ? savedSite
          : hasAccessToSite(location)
            ? location
            : (accessibleSites[0]?.site_name || '');

        if (resolvedLocation !== location) {
          setLocation(resolvedLocation);
        }

        if (resolvedLocation) {
          try {
            localStorage.setItem(getLastSelectedSiteStorageKey(user.email), resolvedLocation);
          } catch (error) {
            console.warn('Failed to store last selected site:', error);
          }
        }
      } catch (err) {
        console.error('Error fetching user sites:', err);
        setSites([]);
      }
    };

    fetchUserSites();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, supabase]); // Don't include location to avoid infinite loops

  // Handle Supabase session
  useEffect(() => {
    const restoreSession = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        setUser(session?.user ?? null);
      } catch (error) {
        console.error('Error restoring session:', error.message);
        setUser(null);
      } finally {
        setLoading(false);
      }
    };

    restoreSession();

    const { data: authListener } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'SIGNED_IN') {
        setUser(session.user);
      } else if (event === 'SIGNED_OUT') {
        setUser(null);
      }
    });

    return () => {
      authListener.subscription.unsubscribe();
    };
  }, [supabase]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress sx={{ color: 'var(--color-brand)' }} />
      </Box>
    );
  }

  return (
    <ErrorBoundary>
      <Router>
        <Routes>
          <Route path="/login" element={<Login setUser={setUser} supabase={supabase} />} />
          <Route
            path="/tintic"
            element={
              user ? (
                <TinticOperationsBoard
                  siteName={tinticSiteName}
                  timezone={tinticTimezone}
                />
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/"
            element={
              user ? (
                <Dashboard
                  supabase={supabase}
                  user={user}
                  setUser={setUser}
                  location={location}
                  setLocation={setLocation}
                  sites={sites}
                />
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
        </Routes>
      </Router>
    </ErrorBoundary>
  );
};

export default App;
